import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Input,
  Badge,
  CardHeader,
  Tooltip,
  CardTitle,
} from "reactstrap";
import AdminHeader from "components/Headers/AdminHeader";
import Select, { components } from "react-select";
import { fixedWidthCustomStyle } from "constants/CommonConstants";
import { APPROVAL_STATUS_OPTIONS } from "components/constant";
import { getPaymentApprovalRequests } from "action/channelPartner";
import { toTitleCase } from "components/CommonComponents/CommonFunctions";
import { paymentApprovalRequestList } from "constants/channelPartner";
import CustomPagination from "components/CommonComponents/ReactTable/CustomPagination";

const ApprovalCourses = () => {
  const dispatch = useDispatch();
  const approvalListData = useSelector(
    (state) => state.channelPartner.paymentApprovalRequestList,
  );
  const [pageNum, setPageNum] = useState(1);

  const [filterState, setFilterState] = useState({
    searchName: "",
    status: null,
    filterParams: [],
  });

  const [listState, setListState] = useState({
    isLoading: true,
    error: "",
    noRecords: false,
    showLoaderOnClick: false,
  });

  const [tooltipOpen, setTooltipOpen] = useState({});

  const toggleTooltip = (key) => {
    setTooltipOpen((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  useEffect(() => {
    getPaymentApprovalRequests(dispatch, pageNum, filterState.filterParams);

    return () => {
      dispatch({
        type: paymentApprovalRequestList,
        payload: {},
      });
    };
  }, [dispatch, pageNum, filterState.filterParams]);

  useEffect(() => {
    if (approvalListData?.code === 200) {
      setListState({
        isLoading: false,
        error: "",
        noRecords:
          approvalListData?.data?.paymentApprovalRequests?.length === 0,
        showLoaderOnClick: false,
      });
    } else if (approvalListData?.code || approvalListData?.message) {
      setListState({
        isLoading: false,
        error:
          approvalListData?.message ||
          "Failed to load approval requests! Please try again.",
        noRecords: false,
        showLoaderOnClick: false,
      });
    }
  }, [approvalListData]);

  const courses = approvalListData?.data?.paymentApprovalRequests || [];

  const totalFilteredRecords =
    approvalListData?.data?.totalFilteredRecords ||
    approvalListData?.data?.totalRecords ||
    0;

  const totalPages = Math.ceil(totalFilteredRecords / 12);

  const Option = (props) => {
    return (
      <components.Option {...props}>
        <Badge
          color={
            props.data.value === "PENDING"
              ? "info"
              : props.data.value === "FAILED"
                ? "danger"
                : "success"
          }
          pill
        >
          {props.data.label}
        </Badge>
      </components.Option>
    );
  };

  const SingleValue = (props) => {
    return (
      <components.SingleValue {...props}>
        <Badge
          color={
            props.data.value === "PENDING"
              ? "info"
              : props.data.value === "FAILED"
                ? "danger"
                : "success"
          }
          pill
        >
          {props.data.label}
        </Badge>
      </components.SingleValue>
    );
  };

  const buildFilterParams = () => {
    const filterParams = [];

    if (filterState.searchName?.trim()) {
      filterParams.push({
        field: "CourseName",
        condition: "like",
        filterValues: [filterState.searchName.trim()],
      });
    }

    if (filterState.status?.value) {
      filterParams.push({
        field: "RequestStatus",
        condition: "eq",
        filterValues: [filterState.status.value],
      });
    }

    return filterParams.length > 0 ? JSON.stringify(filterParams) : [];
  };



  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  const handleSearch = () => {
    const newFilterParams = buildFilterParams();

    if (
      JSON.stringify(filterState.filterParams) !==
      JSON.stringify(newFilterParams)
    ) {
      setListState((prev) => ({
        ...prev,
        isLoading: true,
        showLoaderOnClick: true,
      }));
      setFilterState((prev) => ({
        ...prev,
        filterParams: newFilterParams,
      }));

      if (pageNum !== 1) {
        setPageNum(1);
      }
    }
  };

  const clearSearch = () => {
    setListState((prev) => ({
      ...prev,
      isLoading: true,
    }));
    setFilterState({
      searchName: "",
      status: null,
      filterParams: [],
    });

    if (pageNum !== 1) setPageNum(1);
  };

  return (
    <>
      <AdminHeader />
      <Container className="mt--6" fluid>
        <Card>
          <CardHeader className="d-flex justify-content-between align-items-center">
            <CardTitle tag="h3" className="mb-0 text-default">
              Payment History
            </CardTitle>
          </CardHeader>
          <CardBody>
            <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap flex-column flex-md-row">
              <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap flex-column flex-md-row">
                <div className="d-flex flex-wrap align-items-center filter-group">
                  <Input
                    type="text"
                    placeholder="Search by course name"
                    className="mr-2 mb-2 filter-input-large"
                    value={filterState.searchName}
                    onChange={(e) =>
                      setFilterState((prev) => ({
                        ...prev,
                        searchName: e.target.value,
                      }))
                    }
                    onKeyDown={handleKeyDown}
                  />

                  <Select
                    className="mr-2 mb-2 filter-select"
                    options={APPROVAL_STATUS_OPTIONS}
                    placeholder="Select status"
                    classNamePrefix="react-select"
                    styles={fixedWidthCustomStyle()}
                    components={{ Option, SingleValue }}
                    value={filterState.status}
                    onChange={(selected) =>
                      setFilterState((prev) => ({
                        ...prev,
                        status: selected,
                      }))
                    }
                    onKeyDown={handleKeyDown}
                  />

                  <Button
                    color="default"
                    onClick={handleSearch}
                    className={`d-flex align-items-center mb-2 filter-btn btn-deep-blue ${listState.showLoaderOnClick ? "search-btn" : ""
                      }`}
                  >
                    {listState.showLoaderOnClick ? (
                      <i className="fa fa-spinner fa-spin loader" />
                    ) : (
                      <>
                        <i className="fa fa-search mr-1" />
                        Search
                      </>
                    )}
                  </Button>

                  {filterState.filterParams.length > 0 && (
                    <Button
                      color="default"
                      className="mb-2 filter-btn btn-simple btn-deep-blue"
                      onClick={clearSearch}
                    >
                      <i className="fa fa-times mr-1" />
                      Clear
                    </Button>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-2 mb-3 mx-1">
              {approvalListData?.data?.totalRecords > 0 ? (
                <>
                  {filterState.filterParams.length > 0 ? (
                    <span className="total-records">
                      Showing {approvalListData?.data?.totalFilteredRecords} of{" "}
                      {approvalListData?.data?.totalRecords} Records
                    </span>
                  ) : (
                    <span className="total-records">
                      Total Records : {approvalListData?.data?.totalRecords}
                    </span>
                  )}
                </>
              ) : null}
            </div>

            {listState.isLoading ? (
              <div className="loaderContainer">
                <i className="fa fa-spinner fa-spin fa-2xl loader" />
              </div>
            ) : listState.error ? (
              <div className="loaderContainer error-style">
                <div className="error-icon">
                  <i className="fa-solid fa-circle-exclamation"></i>
                </div>
                <span>{listState.error}</span>
              </div>
            ) : listState.noRecords ? (
              <div className="loaderContainer empty-style">
                <div className="empty-icon">
                  <i className="fa-solid fa-circle-info"></i>
                </div>
                <span>No pending or rejected courses found</span>
              </div>
            ) : (
              <>
                <Row>
                  {courses.map((course) => (
                    <Col md="6" lg="6" xl="3" key={course.ID} className="mb-4">
                      <Card className="course-card h-100 mb-0">
                        <div className="course-image-wrapper">
                          <img
                            src={course.courseThumbnail}
                            alt="Course Thumbnail"
                            className="course-image"
                          />
                          <Badge
                            color={
                              course.requestStatus === "PENDING"
                                ? "info"
                                : course.requestStatus === "FAILED"
                                  ? "danger"
                                  : "success"
                            }
                            className="approval-status-badge"
                            pill
                          >
                            {course.requestStatus === "PENDING"
                              ? "Pending for Approval"
                              : course.requestStatus === "FAILED"
                                ? "Payment Rejected"
                                : course.requestStatus === "CAPTURED"
                                  ? "Payment Approved"
                                  : course.requestStatus}
                          </Badge>
                        </div>

                        <CardBody className="pt-3 pb-1 ml--1 mr--1 d-flex flex-column">
                          {/* Course Title */}
                          <div className="small-text text-deep-blue font-weight-bold">
                            <div
                              id={`courseTitleTooltip-${course.ID}`}
                              className="course-title-ellipsis"
                            >
                              {course.courseName}
                            </div>
                            <Tooltip
                              className="qb-tooltip"
                              placement="auto"
                              delay={{ show: 150, hide: 0 }}
                              target={`courseTitleTooltip-${course.ID}`}
                              isOpen={tooltipOpen[`title-${course.ID}`]}
                              toggle={() => toggleTooltip(`title-${course.ID}`)}
                              autohide={false}
                            >
                              {course.courseName}
                            </Tooltip>
                          </div>

                          <div className="mt-2">
                            {/* Level */}
                            <div className="d-flex align-items-start">
                              <div className="text-muted small mr-2 pt-1">
                                Level:
                              </div>
                              <div className="xs-text pt-1">
                                {toTitleCase(course.courseLevel) || "-"}
                              </div>
                            </div>

                            {/* Batch */}
                            <div className="d-flex align-items-start">
                              <div className="text-muted small mr-2 pt-1">
                                Batch:
                              </div>
                              <div className="flex-grow-1 batch-name-ellipsis-channel-partner">
                                <div
                                  id={`batchNameTooltip-${course.ID}`}
                                  className="xs-text pt-1"
                                >
                                  {course.batchName}
                                </div>

                                <Tooltip
                                  className="qb-tooltip"
                                  placement="auto"
                                  delay={{ show: 150, hide: 0 }}
                                  target={`batchNameTooltip-${course.ID}`}
                                  isOpen={tooltipOpen[`batch-${course.ID}`]}
                                  toggle={() =>
                                    toggleTooltip(`batch-${course.ID}`)
                                  }
                                  autohide={false}
                                >
                                  {course.batchName}
                                </Tooltip>
                              </div>
                            </div>

                            {/* Payment */}
                            {/* <div className="approval-date-item">
                              <div className="text-muted small">
                                Payment Date:
                              </div>
                              <div className="xs-text">
                                {course.paymentDate || "-"}
                              </div>
                            </div> */}

                            {/* Submitted */}
                            <div className="d-flex align-items-start flex-wrap">
                              <div className="text-muted small mr-2 pt-1 text-nowrap">
                                Submitted Date:
                              </div>
                              <div className="xs-text pt-1 text-nowrap">
                                {course.submittedDate || "-"}
                              </div>
                            </div>
                          </div>

                          <div className="mt-2 pt-2 flex-grow-1 d-flex flex-column">
                            <div className="mb-2 w-100">
                              <div className="payment-summary-box">
                                <div className="payment-summary-row">
                                  <span className="text-muted small">
                                    Total Amount:
                                  </span>
                                  <span className="xs-text">
                                    &#8377;{course.totalAmount}
                                  </span>
                                </div>
                                <div className="payment-summary-row">
                                  <span className="text-muted small">
                                    Total GST:
                                  </span>
                                  <span className="xs-text">
                                    &#8377;{course.totalGstAmount}
                                  </span>
                                </div>
                                <div className="payment-summary-total">
                                  <span className="text-muted font-weight-bold small">
                                    Payable Amount:
                                  </span>
                                  <span className="font-weight-bold text-dark small">
                                    &#8377;{course.payableAmount}
                                  </span>
                                </div>
                              </div>
                            </div>

                            <div className="mt-auto">
                              {course.rejectionReason ? (
                                <div className={`${course.requestStatus === "FAILED" ? "approval-reason-box" : "approval-remark-box"}  text-sm mt-0 w-100`}>
                                  <strong className={`${course.requestStatus === "FAILED" ? "text-danger" : "text-success"} d-block mb-1`}>
                                    {course.requestStatus === "FAILED" ? "Rejection Reason:" : "Remark"}
                                  </strong>
                                  <span
                                    id={`rejectionReasonTooltip-${course.ID}`}
                                    className="text-dark rejection-reason-ellipsis"
                                  >
                                    {course.rejectionReason}
                                  </span>

                                  <Tooltip
                                    className="qb-tooltip"
                                    placement="auto"
                                    delay={{ show: 150, hide: 0 }}
                                    target={`rejectionReasonTooltip-${course.ID}`}
                                    isOpen={
                                      tooltipOpen[`rejection-${course.ID}`]
                                    }
                                    toggle={() =>
                                      toggleTooltip(`rejection-${course.ID}`)
                                    }
                                    autohide={false}
                                  >
                                    {course.rejectionReason}
                                  </Tooltip>
                                </div>
                              ) : (
                                <div className="approval-info-box text-sm mt-0 w-100">
                                  <strong className="text-primary d-block mb-1">
                                    Current Status:
                                  </strong>
                                  <span className="text-dark rejection-reason-ellipsis">
                                    {course.requestStatus === "CAPTURED"
                                      ? "Payment successfully captured."
                                      : "Awaiting admin review."}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        </CardBody>
                      </Card>
                    </Col>
                  ))}
                </Row>
                <CustomPagination
                  totalPages={totalPages}
                  activePage={pageNum}
                  setPageNum={setPageNum}
                />
              </>
            )}
          </CardBody>
        </Card>
      </Container>
    </>
  );
};

export default ApprovalCourses;
